import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { getInheritedSettings, getSourceAndLabel } from '../PositionRateSettingsForm.utils';
import { calculatedFieldPath } from 'resources/rate_settings/utils/fieldsVisibility';
import { useScalingMarginEffect } from './useScalingMarginEffect';

type UseRateCalculationsProps = {
  source: string;
  inheritedSettings: any;
};

export const useRateCalculations = ({ source, inheritedSettings }: UseRateCalculationsProps) => {
  const { watch, setValue, setError, clearErrors } = useFormContext();

  const mergedSettings = getInheritedSettings({ source, inheritedSettings, watch });
  const { field } = getSourceAndLabel(source);
  const isW2Local = source.includes('w2.local');

  const applicableField = isW2Local ? 'base_margin_percent_w2' : field;

  const chargeRateCents = watch(calculatedFieldPath(source, 'charge_rate_cents', watch));
  const proRateCents = watch(`${source}.pro_rate_cents`);
  const blendedRateCents = watch(`${source}.blended_rate_cents`);
  const blendedRateMarkup = watch(`${source}.blended_rates.blended_rate_markup`);
  const useBlendedRate = watch(`${source}.use_blended_rate`);
  const marginPercent = watch(calculatedFieldPath(source, applicableField, watch));

  const watchedFields = useMemo(
    () => ({
      chargeRateCents,
      proRateCents,
      blendedRateCents,
      blendedRateMarkup,
      useBlendedRate,
      marginPercent,
    }),
    [
      chargeRateCents,
      proRateCents,
      blendedRateCents,
      blendedRateMarkup,
      useBlendedRate,
      marginPercent,
    ],
  );

  const { validateMarginPercentGuardrail, validateIncrementalMargin } = useScalingMarginEffect({
    setError,
    clearErrors,
    watch,
    setValue,
    source,
    applicableField,
    isW2Local,
  });

  return {
    watchedFields,
    mergedSettings,
    validations: {
      validateMarginPercentGuardrail,
      validateIncrementalMargin,
    },
  };
};
